'use client';

// Keeps the Datadog RUM user in step with the signed-in profile. Renders nothing.
//
// Mounted directly under the QueryClientProvider in Providers — NOT inside the
// LaunchDarkly gate. That gate's profile-aware branch only renders when LD is
// configured and NEXT_PUBLIC_INTEGRATIONS_ENABLED is off, so a RumUser placed
// there is compiled out of builds that set the integrations flag (it shipped
// that way once, and no session carried a user). The profile query shares the
// ['profile'] key, so this reuses the same fetch rather than adding one.
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { clearRumUser, identifyRumUser } from '@/observability/rum';
import { useCurrentUser, type CurrentUser, type ProfileCompany } from '@/lib/data/profile';
import { isGuestPath } from '@/lib/auth/guest-paths';

export function RumUser() {
  const pathname = usePathname();
  const { data, isLoading } = useCurrentUser({ enabled: !isGuestPath(pathname) });
  return <RumUserSync user={data?.user} company={data?.company} settled={!isLoading} />;
}

export interface RumUserSyncProps {
  user: CurrentUser | undefined;
  company: ProfileCompany | undefined;
  /** False while the profile is still loading — no user then means "not known yet", not "signed out". */
  settled: boolean;
}

export function RumUserSync({ user, company, settled }: RumUserSyncProps): null {
  const id = user?.id;
  const email = user?.email;
  const firstName = user?.firstName;
  const lastName = user?.lastName;
  const companyId = company?.id;
  const companyName = company?.name;

  useEffect(() => {
    if (id) {
      identifyRumUser({
        id,
        email: email ?? '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        companyId,
        companyName,
      });
    } else if (settled) {
      clearRumUser();
    }
  }, [id, email, firstName, lastName, companyId, companyName, settled]);

  return null;
}
